import { Transacao } from '../../../servicos/transacoes/typeTransacao';
import { ehPagamentoFatura, primeiroDiaMesAtual, ultimoDiaMesAtual } from '../../../utils/Utils';

/** Dia do mês respeitando meses curtos (fechamento dia 31 em fevereiro → dia 28/29). */
export const dataComDia = (ano: number, mes: number, dia: number): Date => {
  const ultimoDia = new Date(ano, mes + 1, 0).getDate();
  return new Date(ano, mes, Math.min(dia, ultimoDia));
};

/**
 * Ciclo corrente da fatura. Convenção dos bancos brasileiros (Nubank, Itaú,
 * Bradesco): o fechamento acontece no INÍCIO do dia — compra feita no próprio
 * dia do fechamento já entra na fatura seguinte. Logo o ciclo vai do dia do
 * fechamento (inclusive) até o dia anterior ao próximo fechamento.
 * Sem dia de fechamento cadastrado, usa o mês civil.
 * `referencia` existe para testes; em produção é o dia de hoje.
 */
export const cicloAtual = (dataFechamento?: number, referencia?: Date): { inicio: Date; fim: Date } => {
  if (!dataFechamento || dataFechamento < 1 || dataFechamento > 31) {
    return { inicio: primeiroDiaMesAtual(), fim: ultimoDiaMesAtual() };
  }

  const agora = referencia ?? new Date();
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const fechamentoDesteMes = dataComDia(hoje.getFullYear(), hoje.getMonth(), dataFechamento);

  if (hoje.getTime() >= fechamentoDesteMes.getTime()) {
    const fim = dataComDia(hoje.getFullYear(), hoje.getMonth() + 1, dataFechamento);
    fim.setDate(fim.getDate() - 1);
    return { inicio: fechamentoDesteMes, fim };
  }

  const inicio = dataComDia(hoje.getFullYear(), hoje.getMonth() - 1, dataFechamento);
  const fim = new Date(fechamentoDesteMes);
  fim.setDate(fim.getDate() - 1);
  return { inicio, fim };
};

/**
 * Ciclo ANTERIOR ao corrente (a fatura que já fechou): termina um dia antes do
 * início do ciclo atual. Sem dia de fechamento, é o mês civil anterior.
 */
export const cicloAnterior = (dataFechamento?: number, referencia?: Date): { inicio: Date; fim: Date } => {
  if (!dataFechamento || dataFechamento < 1 || dataFechamento > 31) {
    const agora = referencia ?? new Date();
    return {
      inicio: new Date(agora.getFullYear(), agora.getMonth() - 1, 1),
      fim: new Date(agora.getFullYear(), agora.getMonth(), 0)
    };
  }
  const atual = cicloAtual(dataFechamento, referencia);
  const fimAnterior = new Date(atual.inicio);
  fimAnterior.setDate(fimAnterior.getDate() - 1);
  return cicloAtual(dataFechamento, fimAnterior);
};

/**
 * Vencimento da fatura que fecha em `fimDoCiclo`: o dia de vencimento cai no
 * mesmo mês se for depois do fechamento; senão, no mês seguinte.
 */
export const vencimentoDoCiclo = (fimDoCiclo: Date, diaVencimento?: number): Date | null => {
  if (!diaVencimento || diaVencimento < 1 || diaVencimento > 31) return null;
  const mesmoMes = diaVencimento > fimDoCiclo.getDate();
  return dataComDia(fimDoCiclo.getFullYear(), fimDoCiclo.getMonth() + (mesmoMes ? 0 : 1), diaVencimento);
};

/**
 * Quanto AINDA falta pagar da fatura fechada: soma do ciclo anterior menos os
 * pagamentos de fatura registrados DEPOIS do fechamento cujo texto cita o
 * cartão (o "Registrar pagamento" do app escreve "Pagamento de fatura · Nome").
 * Nunca negativo. Heurística honesta: pagamento lançado sem citar o nome do
 * cartão não abate — a linha da fatura fechada continua visível.
 */
export const faturaFechadaAPagar = (
  transacoes: Transacao[],
  cartaoId: number,
  nomeCartao: string,
  janelaAnterior: { inicio: Date; fim: Date }
): number => {
  const fatura = faturaDoCiclo(transacoes, cartaoId, janelaAnterior);
  if (fatura <= 0) return 0;

  const nome = nomeCartao.trim().toLowerCase();
  const pagamentos = transacoes
    .filter((t) => {
      if (!ehPagamentoFatura(t) || t.tipo !== 'D') return false;
      if (!nome || !(t.descricao ?? '').toLowerCase().includes(nome)) return false;
      return dataDaTransacao(t.data).getTime() > janelaAnterior.fim.getTime();
    })
    .reduce((soma, t) => soma + Number(t.valor), 0);

  return Math.max(0, fatura - pagamentos);
};

// Converte a data da transação (Date, "dd/mm/aaaa" ou ISO) para meia-noite local.
export const dataDaTransacao = (data: Date | string): Date => {
  if (data instanceof Date) return new Date(data.getFullYear(), data.getMonth(), data.getDate());
  const texto = String(data);
  if (texto.includes('/')) {
    const [dia, mes, ano] = texto.split('/').map(Number);
    return new Date(ano, mes - 1, dia);
  }
  const convertida = new Date(texto);
  return new Date(convertida.getFullYear(), convertida.getMonth(), convertida.getDate());
};

/**
 * Fatura estimada de um cartão no ciclo: soma as despesas e abate as receitas
 * (estornos) das transações do cartão dentro da janela [inicio, fim].
 * Pagamento de fatura NUNCA entra aqui — é quitação, não compra; mesmo que
 * venha com cartaoCreditoId por engano, não pode inflar a fatura.
 */
export const faturaDoCiclo = (
  transacoes: Transacao[],
  cartaoId: number,
  ciclo: { inicio: Date; fim: Date }
): number =>
  transacoes
    .filter((transacao) => transacao.cartaoCreditoId === cartaoId)
    .filter((transacao) => !ehPagamentoFatura(transacao))
    .filter((transacao) => {
      const data = dataDaTransacao(transacao.data);
      return data.getTime() >= ciclo.inicio.getTime() && data.getTime() <= ciclo.fim.getTime();
    })
    .reduce((soma, transacao) => soma + (transacao.tipo === 'D' ? transacao.valor : -transacao.valor), 0);
